import { useReactiveVar } from '@apollo/client';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { FC, useMemo, useRef } from 'react';
import useIcons from '../../../../hooks/useIcons';
import { vars } from '../../../../reactive';
import LoadingSpinner from '../../../Shared/LoadingSpinner/LoadingSpinner';
import { ErrorMessage } from '../../../Shared/Shared';
import { MON_THREE_DAYS, THREE_DAYS, checkIfSlotAvailable, compareDatesWithoutTime, findBookingDate, getMonth, getUTCTime, sideScroll } from '../../helpers';
import { FORM_RADIO_COMPONENT_TYPES, FORM_RADIO_TYPES, FormDateAndTime } from '../../types';
import FormRadioDateButton from './FormRadioDateButton';
import {
  BookingButton,
  BookingButtonContainer,
  DayLabel,
  DaysLabels,
  FormDateContainer,
  FormDateLabel,
  FormDateLabelContainer,
  FormMonth,
  FormMonthTitle,
  FormMonths,
  FormMonthsContainer,
  ScrollButton,
  ScrollButtonContainer,
  VerticalLine
} from './styled';

interface FormRadioDateProps {
  type: keyof typeof FORM_RADIO_COMPONENT_TYPES;
  formOptions: FormDateAndTime;
  bookings: Record<string, any>;
  loading: boolean;
  session: number;
  watchedDates: (string | Date)[][] | (string | Date)[];
  orderGroupIndex: number;
  onLoadMore?: () => void;
  hasMoreSlots?: boolean;
}

const FormRadioDate: FC<FormRadioDateProps> = ({ type, bookings, formOptions, loading, session, watchedDates, orderGroupIndex, onLoadMore, hasMoreSlots }) => {
  const { registerAs, errorMessage } = FORM_RADIO_COMPONENT_TYPES[type];
  const { formState } = formOptions;
  const bookingMonths = useMemo(() => Object.keys(bookings)?.sort((a: any, b: any) => a - b), [bookings]);
  const isMultiday = type === FORM_RADIO_TYPES.DATE_UNTIL;
  const containerRef = useRef<HTMLDivElement>(null);
  const icons = useIcons();
  const arrowRight = getImage(icons?.arrowRight)!;
  const arrowLeft = getImage(icons?.arrowLeft)!;
  const selectedOrderGroupSlots = useReactiveVar(vars.selectedOrderGroupSlots);
  const selectedSlots = selectedOrderGroupSlots?.[orderGroupIndex]?.slots || [];

  const checkIfAvailable = (fullDate: Date) => {
    const allTheDateSlots = findBookingDate(bookings, fullDate)?.slots.filter(({ available }) => available);
    const slotsInDay = allTheDateSlots?.map(getUTCTime);
    const selectedSlotsInOtherGroups = selectedOrderGroupSlots?.filter((_, index) => index !== orderGroupIndex).flatMap(group => group.slots) || [];
    const selectedSltosInOtherSessions = selectedSlots.filter((_, index) => index !== session);
    const selectedSlotsInDay = [...selectedSltosInOtherSessions, ...selectedSlotsInOtherGroups].filter(({ date, startDate }) =>
      compareDatesWithoutTime(date?.fullDate || startDate?.fullDate!, fullDate)
    );

    const selectedSlotsInDayTimes = selectedSlotsInDay.map(slot => slot.time);
    const everySlotBookedInDay = slotsInDay?.every((slot: any) => selectedSlotsInDayTimes?.includes(slot));

    return checkIfSlotAvailable(bookings, fullDate) && !everySlotBookedInDay;
  };

  return (
    <>
      <FormMonthsContainer isMultiday={isMultiday}>
        {!isMultiday && (
          <ScrollButtonContainer>
            <ScrollButton type="button" onClick={() => sideScroll(containerRef.current as any, 'left')}>
              <GatsbyImage image={arrowLeft} alt="arrow left" />
            </ScrollButton>
          </ScrollButtonContainer>
        )}

        {isMultiday && (
          <DaysLabels>
            {MON_THREE_DAYS.map(day => {
              return <DayLabel key={day}>{day}</DayLabel>;
            })}
          </DaysLabels>
        )}

        <FormMonths isMultiday={isMultiday} ref={containerRef}>
          {bookingMonths.map(bookingMonth => {
            const firstDay = bookings[bookingMonth]?.[0]?.day;
            const lastDay = bookings[bookingMonth]?.[bookings[bookingMonth]?.length - 1]?.day;
            const dayIndex = THREE_DAYS.indexOf(firstDay);
            const offset = dayIndex === 0 ? 6 : dayIndex - 1;
            const emptyStartingDays = [...Array(offset).keys()];
            const emptyEndingDays = [...Array(7 - THREE_DAYS.indexOf(lastDay)).keys()];

            return (
              <FormMonth key={`${session}_${bookingMonth}`} isMultiday={isMultiday}>
                <FormMonthTitle>{getMonth(bookingMonth, { includeYear: isMultiday })}</FormMonthTitle>
                <FormDateContainer isMultiday={isMultiday}>
                  <FormDateLabelContainer isMultiday={isMultiday}>
                    {isMultiday &&
                      emptyStartingDays.map(day => (
                        <FormDateLabel key={`${session}_${bookingMonth}_${day}`} isMultiday>
                          <BookingButtonContainer isMultiday>
                            <BookingButton disabled circle empty />
                          </BookingButtonContainer>
                        </FormDateLabel>
                      ))}

                    {bookings[bookingMonth]?.map((booking: Record<string, any>) => {
                      return (
                        <FormRadioDateButton
                          key={`${session}_${bookingMonth}_${booking.date}`}
                          booking={booking}
                          formOptions={formOptions}
                          bookingMonth={bookingMonth}
                          checkIfAvailable={checkIfAvailable}
                          bookings={bookings}
                          loading={loading}
                          session={session}
                          type={type}
                          watchedDates={watchedDates}
                          selectedSlots={selectedSlots}
                        />
                      );
                    })}

                    {isMultiday &&
                      emptyEndingDays.map((day, index) => (
                        <FormDateLabel key={`${session}_${bookingMonth}_${day}`} isMultiday isLastDayInRow={index === emptyEndingDays.length - 1}>
                          <BookingButtonContainer isMultiday>
                            <BookingButton disabled hidden circle />
                          </BookingButtonContainer>
                        </FormDateLabel>
                      ))}
                  </FormDateLabelContainer>
                </FormDateContainer>
                {!isMultiday && <VerticalLine />}
              </FormMonth>
            );
          })}
          {!isMultiday && hasMoreSlots && (
            <FormDateLabelContainer isMultiday={isMultiday}>
              <FormDateLabel isMultiday>
                <BookingButtonContainer isMultiday>
                  <BookingButton type="button" onClick={onLoadMore} disabled={loading} loading={loading} pill>
                    {loading ? <LoadingSpinner /> : 'Load more'}
                  </BookingButton>
                </BookingButtonContainer>
              </FormDateLabel>
            </FormDateLabelContainer>
          )}
          {!hasMoreSlots && !Object.keys(bookings || {}).length && (
            <FormDateLabelContainer isMultiday={isMultiday}>
              <FormDateLabel isMultiday>
                <BookingButtonContainer isMultiday>No available slots</BookingButtonContainer>
              </FormDateLabel>
            </FormDateLabelContainer>
          )}
        </FormMonths>
        {!isMultiday && (
          <ScrollButtonContainer>
            <ScrollButton type="button" onClick={() => sideScroll(containerRef.current as HTMLDivElement, 'right')}>
              <GatsbyImage image={arrowRight} alt="arrow right" />
            </ScrollButton>
          </ScrollButtonContainer>
        )}
      </FormMonthsContainer>
      {formState?.errors?.[registerAs] && <ErrorMessage>{`${formState?.errors?.[registerAs]?.message || errorMessage}`}</ErrorMessage>}
    </>
  );
};

export default FormRadioDate;
